<template>
  <Transition>
    <IconButton
      v-if="isNotificationSettingsLoaded && isVisible"
      class="RunNotificationToggleView"
      :iconName="isNotificationsEnabled ? 'bellRing' : 'bellSlash'"
      :aria-label="isNotificationsEnabled ? 'Mute notifications' : 'Enable notifications'"
      @click="handleNotificationToggle"
    />
  </Transition>
</template>

<script lang="ts" setup>
import IconButton from "@/components/IconButton.vue";
import { supabaseClient } from "@/lib/database/supabase";
import haptic from "@/lib/utils/platform/haptic";
import useAuthStore from "@/stores/useAuthStore";
import { storeToRefs } from "pinia";
import { ref, watch } from "vue";

const {
  runId,
  isVisible = true,
} = defineProps<{
  runId: string,
  isVisible?: boolean,
}>();

const {
  currentUser,
} = storeToRefs(useAuthStore());

const isNotificationSettingsLoaded = ref(false);
const isNotificationsEnabled = ref(false);
const isUpdatingNotificationSettings = ref(false);

watch(() => currentUser.value?.id, async (userId) => {
  if (userId) {
    isNotificationSettingsLoaded.value = false;

    const {
      data: notificationSetting,
      error,
    } = await supabaseClient
      .from("run_notification_settings")
      .select()
      .match({
        user_id: userId,
        run_id: runId,
      })
      .maybeSingle();

    if (!error) {
      isNotificationsEnabled.value = !notificationSetting || notificationSetting.is_enabled;
      isNotificationSettingsLoaded.value = true;
    }
  }
}, {
  immediate: true,
});

async function handleNotificationToggle () {
  if (!currentUser.value || isUpdatingNotificationSettings.value) return;

  haptic();

  isUpdatingNotificationSettings.value = true;
  const {
    error,
  } = await supabaseClient
    .from("run_notification_settings")
    .upsert({
      user_id: currentUser.value.id,
      run_id: runId,
      is_enabled: !isNotificationsEnabled.value,
    })
    .select()
    .maybeSingle();

  isUpdatingNotificationSettings.value = false;

  if (error) {
    alert(error.message);
  } else {
    isNotificationsEnabled.value = !isNotificationsEnabled.value;
  }
}
</script>

<style lang="scss" scoped>
.RunNotificationToggleView {
  color: var(--localColorSecondaryFill);
  transition: 250ms;
  transition-property: opacity, transform;

  &:disabled {
    opacity: 0.5;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}
</style>
